/**
 * Browser error reporting (TZ §13.2).
 *
 * Off unless the backend has a browser DSN. The DSN is read at runtime from
 * `/api/public/config/`, never baked into the bundle, so switching it on or off is a
 * configuration change rather than a rebuild. Sourcemaps are not uploaded: that needs a
 * Sentry auth token, which this build does not hold. Stack traces therefore name minified
 * positions until a release is run with `sentry-cli` against the production project.
 *
 * What leaves the browser is scrubbed here, before it is sent:
 *   - no user (email, ip) and no request body, cookies or headers;
 *   - no breadcrumb data, which can carry form values;
 *   - any tag, extra or context key that names a secret or a person is removed;
 *   - phone numbers and passport-shaped strings are masked in every remaining string.
 */
import * as Sentry from "@sentry/react";

const BASE_URL: string = import.meta.env.VITE_API_BASE_URL;
const CONFIG_PATH = "/api/public/config/";

/** Keys whose values never leave the browser, whatever they hold. */
const SENSITIVE_KEY = /token|password|passwd|secret|key|authorization|cookie|session|passport|birth|phone|email/i;
/** Uzbek mobile numbers, written with or without separators, and plus-numbers. */
const PHONE = /\+998[\s-]?\d{2}[\s-]?\d{3}[\s-]?\d{2}[\s-]?\d{2}\b|\+\d{7,15}\b/g;
const PASSPORT = /\b[A-Z]{2}\d{7}\b/g;

export interface BrowserErrorReporting {
  dsn: string;
  environment: string;
  release: string;
}

interface PublicConfigBody {
  sentry: BrowserErrorReporting;
}

export function maskPersonalText(text: string): string {
  return text
    .replace(PHONE, (match) => `***${match.replace(/\D/g, "").slice(-4)}`)
    .replace(PASSPORT, (match) => `${match.slice(0, 2)}*****${match.slice(-2)}`);
}

function scrubRecord(record: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (record === undefined) {
    return undefined;
  }
  const kept: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (SENSITIVE_KEY.test(key)) {
      continue;
    }
    kept[key] = typeof value === "string" ? maskPersonalText(value) : value;
  }
  return kept;
}

/**
 * The Sentry `beforeSend` hook. Pure: it returns a new event and never drops one, because a
 * lost report of a real failure is worse than a masked one.
 */
export function scrubEvent<E extends Partial<Sentry.ErrorEvent>>(event: E): E {
  const request = event.request
    ? { url: event.request.url, method: event.request.method }
    : undefined;
  const breadcrumbs = event.breadcrumbs?.map((crumb) => ({
    category: crumb.category,
    level: crumb.level,
    timestamp: crumb.timestamp,
    message: crumb.message === undefined ? undefined : maskPersonalText(crumb.message),
  }));
  const exception = event.exception
    ? {
        ...event.exception,
        values: event.exception.values?.map((value) => ({
          ...value,
          value: value.value === undefined ? undefined : maskPersonalText(value.value),
        })),
      }
    : undefined;

  const scrubbed = {
    ...event,
    user: undefined,
    request,
    breadcrumbs,
    exception,
    message: event.message === undefined ? undefined : maskPersonalText(event.message),
    tags: scrubRecord(event.tags),
    extra: scrubRecord(event.extra),
    contexts: scrubRecord(event.contexts),
  };
  // The keys above keep their names and shape; only their contents are filtered.
  return scrubbed;
}

async function fetchReporting(): Promise<BrowserErrorReporting> {
  const response = await fetch(`${BASE_URL}${CONFIG_PATH}`, {
    headers: { Accept: "application/json" },
    credentials: "omit",
  });
  if (!response.ok) {
    throw new Error(`public config answered HTTP ${response.status}`);
  }
  const body = (await response.json()) as PublicConfigBody;
  return body.sentry;
}

/**
 * Starts Sentry when the backend names a browser DSN. Resolves without starting when it does
 * not. A failed config request rejects: the caller decides how to surface it, and the app
 * renders either way.
 */
export async function startErrorReporting(): Promise<boolean> {
  const reporting = await fetchReporting();
  if (reporting.dsn === "") {
    return false;
  }
  Sentry.init({
    dsn: reporting.dsn,
    environment: reporting.environment,
    release: reporting.release === "" ? undefined : reporting.release,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    beforeSend: (event) => scrubEvent(event),
  });
  return true;
}
